import { JointGiftingApprovalStatus } from '@interfaces';
import { GiftRecipientTypes, YesNoEmpty } from '@interfaces/models';
import { Action, createReducer, on } from '@ngrx/store';

import {
  JointGiftingApprovalCompleted,
  JointGiftingApprovalFailed,
  JointGiftingApprovalInProgress,
  ResetRecipientState,
  UpdateGiftId,
  UpdateRecipientAccountNumber,
  UpdateRecipientAnonymousGift,
  UpdateRecipientDTCNumber,
  UpdateRecipientFirm,
  UpdateRecipientName,
  UpdateRecipientProperties,
  UpdateRecipientType,
  UpdateSignState,
  UpdateTotalGiftAmount,
} from '../../actions/gifting/gifting.actions';
export { JointGiftingApprovalStatus } from '@interfaces';
import {
  GiftingRecipient as _GiftingRecipient,
  GiftingState as _GiftingState,
} from '@interfaces/state';

export interface GiftingState extends _GiftingState {}
export interface GiftingRecipient extends _GiftingRecipient {}

export const initialState: GiftingState = {
  recipient: {
    anonymousGift: YesNoEmpty.EMPTY,
    dtcNumber: '',
    type: GiftRecipientTypes.EMPTY,
    firm: '',
    name: '',
    accountNumber: '',
  },
  jointGiftingApprovalStatus: JointGiftingApprovalStatus.EMPTY,
  totalGiftAmount: '',
};

const configReducer = createReducer(
  initialState,
  on(ResetRecipientState, (state) => ({
    ...state,
    recipient: {
      ...initialState.recipient,
    },
  })),
  on(UpdateRecipientAnonymousGift, (state, { payload }) => ({
    ...state,
    recipient: {
      ...state.recipient,
      anonymousGift: payload,
    },
  })),
  on(UpdateRecipientDTCNumber, (state, { payload }) => ({
    ...state,
    recipient: {
      ...state.recipient,
      dtcNumber: payload,
    },
  })),
  on(UpdateRecipientType, (state, { payload }) => ({
    ...state,
    recipient: {
      ...state.recipient,
      type: payload,
    },
  })),
  on(UpdateRecipientFirm, (state, { payload }) => ({
    ...state,
    recipient: {
      ...state.recipient,
      firm: payload,
    },
  })),
  on(UpdateRecipientName, (state, { payload }) => ({
    ...state,
    recipient: {
      ...state.recipient,
      name: payload,
    },
  })),
  on(UpdateRecipientAccountNumber, (state, { payload }) => ({
    ...state,
    recipient: {
      ...state.recipient,
      accountNumber: payload,
    },
  })),
  on(UpdateTotalGiftAmount, (state, { payload }) => ({
    ...state,
    totalGiftAmount: payload,
  })),
  on(UpdateRecipientProperties, (state, { payload }) => ({
    ...state,
    recipient: {
      ...state.recipient,
      ...payload,
    },
  })),
  on(JointGiftingApprovalInProgress, (state) => {
    return {
      ...state,
      jointGiftingApprovalStatus: JointGiftingApprovalStatus.PENDING,
    };
  }),
  on(JointGiftingApprovalCompleted, (state) => {
    return {
      ...state,
      jointGiftingApprovalStatus: JointGiftingApprovalStatus.SUCCESS,
    };
  }),
  on(JointGiftingApprovalFailed, (state) => {
    return {
      ...state,
      jointGiftingApprovalStatus: JointGiftingApprovalStatus.ERROR,
    };
  }),
  on(UpdateGiftId, (state, { payload }) => ({
    ...state,
    giftId: payload,
  })),
  on(UpdateSignState, (state, { payload }) => ({
    ...state,
    signState: payload,
  })),
);

export function reducer(state: GiftingState | undefined, action: Action) {
  return configReducer(state, action);
}

export const getRecipientAnonymousGift = (recipient: GiftingRecipient) =>
  recipient ? recipient.anonymousGift : null;
export const getRecipientDTCNumber = (recipient: GiftingRecipient) =>
  recipient ? recipient.dtcNumber : null;
export const getRecipientType = (recipient: GiftingRecipient) =>
  recipient ? recipient.type : null;
export const getRecipientFirm = (recipient: GiftingRecipient) =>
  recipient ? recipient.firm : null;
export const getRecipientName = (recipient: GiftingRecipient) =>
  recipient ? recipient.name : null;
export const getRecipientAccountNumber = (recipient: GiftingRecipient) =>
  recipient ? recipient.accountNumber : null;
export const getRecipientTransferAgentName = (recipient: GiftingRecipient) =>
  recipient ? recipient.transferAgentName : null;
export const getRecipientStreetAddress = (recipient: GiftingRecipient) =>
  recipient ? recipient.streetAddress : null;
export const getRecipientZipcode = (recipient: GiftingRecipient) =>
  recipient ? recipient.zipcode : null;
export const getRecipientState = (recipient: GiftingRecipient) =>
  recipient ? recipient.state : null;
export const getRecipientCity = (recipient: GiftingRecipient) =>
  recipient ? recipient.city : null;
export const getRecipientSSN = (recipient: GiftingRecipient) => (recipient ? recipient.ssn : null);
export const getRecipientDateOfBirth = (recipient: GiftingRecipient) =>
  recipient ? recipient.dateOfBirth : null;
export const getTotalGiftAmount = (state: GiftingState) => state.totalGiftAmount;

export const getIsCharityRecipient = (recipient: GiftingRecipient) =>
  getRecipientType(recipient) === GiftRecipientTypes.CHARITY;
export const getIsBrokerageFirmRecipient = (recipient: GiftingRecipient) =>
  getRecipientType(recipient) === GiftRecipientTypes.BROKERAGE_FIRM;
export const getIsTransferAgentRecipient = (recipient: GiftingRecipient) =>
  getRecipientType(recipient) === GiftRecipientTypes.TRANSFER_AGENT;

export const getJointGiftingApprovalStatus = (state: GiftingState) =>
  state ? state.jointGiftingApprovalStatus : null;

export const getGiftId = (state: GiftingState) => (state ? state.giftId : null);
export const getSignState = (state: GiftingState) => (state ? state.signState : null);
